import { Component } from '@theme/component';
import { onAnimationEnd, updateAllHeaderCustomProperties } from '@theme/utilities';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  #isDismissed = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    // Persistence disabled on request.
    // if (this.dismissed) {
    //   this.#isDismissed = true;
    //   this.#hideSection();
    //   return;
    // }

    this.querySelector('[data-announcement-bar-close]')?.addEventListener('click', this.dismiss);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    this.querySelector('[data-announcement-bar-close]')?.removeEventListener('click', this.dismiss);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {

    console.log('this.autoplay', this.autoplay);
    console.log('play', interval, this.autoplayInterval);

    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  // Persistence disabled on request.
  // get dismissKey() {
  //   const sectionId = this.dataset.sectionId;
  //   if (!sectionId) return undefined;

  //   return `announcement-bar-dismissed-${sectionId}`;
  // }

  // get dismissed() {
  //   if (window.Shopify?.designMode || !this.dismissKey) return false;

  //   try {
  //     return localStorage.getItem(this.dismissKey) === 'true';
  //   } catch (_error) {
  //     return false;
  //   }
  // }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  dismiss = () => {
    if (this.#isDismissed) return;

    this.#isDismissed = true;
    this.pause();

    this.#hideSection();
  };

  #hideSection() {
    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) return;

    const currentHeight = section.offsetHeight;
    section.style.height = `${currentHeight}px`;

    // Commit the measured height before transitioning to zero.
    section.offsetHeight;

    section.classList.add('announcement-bar-section--closing');

    requestAnimationFrame(() => {
      section.style.height = '0px';
    });

    onAnimationEnd(section, () => {
      section.hidden = true;
      section.classList.remove('announcement-bar-section--closing');
      section.style.removeProperty('height');
      updateAllHeaderCustomProperties();
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
